import type { ReactNode } from 'react';
import ConfirmationModal from 'components/common/ConfirmationModal';
import { waitDialog } from './waitDialog';

interface ConfirmDialogArgs {
  readonly title: ReactNode;
  readonly body: ReactNode;
  readonly confirmText?: string;
  readonly confirmVariant?: string;
}

/**
 * Show a yes/no confirm dialog and await the user's answer. Returns
 * `true` if the user confirmed, `false` if they cancelled (closed with
 * X, backdrop, or the cancel button) — `waitDialog<boolean>`'s
 * `undefined` cancel signal is normalised to `false` so callers can
 * write `if (await confirmDialog(...)) { ... }`.
 *
 * Wraps the project's existing `ConfirmationModal`; the cancel button
 * label stays as ConfirmationModal's default ("Cancel"). Pass
 * `confirmVariant: 'danger'` for destructive confirmations.
 */
export const confirmDialog = async (args: ConfirmDialogArgs): Promise<boolean> => {
  const result = await waitDialog<true>((ok, cancel) => (
    <ConfirmationModal
      show={true}
      title={args.title}
      body={args.body}
      confirmText={args.confirmText}
      confirmVariant={args.confirmVariant}
      onConfirm={() => ok(true)}
      onCancel={cancel}
    />
  ));
  return result === true;
};
